<template>
  <div v-if="hasItems" class="psychology-attachment-viewer">
    <!-- 1. 图片类展示（支持多图缩略图 + 点击全屏大图预览切换） -->
    <div v-if="images.length > 0" class="pav-image-section">
      <div v-if="showSectionTitle" class="pav-section-title">
        <i class="el-icon-picture-outline"></i>
        <span>照片 / 图像材料</span>
        <small class="pav-count">({{ images.length }})</small>
      </div>
      <div class="pav-image-grid" :class="{ 'is-compact': layout === 'compact' }">
        <oss-el-image
          v-for="(img, idx) in images"
          :key="'pimg-' + idx"
          class="pav-image-item"
          :style="{ width: imgSizePx, height: imgSizePx }"
          :src="img.FileUrl"
          :preview-src-list="imageUrls"
          fit="cover"
          title="点击放大预览"
        />
      </div>
    </div>

    <!-- 2. 文档附件类展示（参考工作记录：格式图标 + 文件名 + 大小 + 预览按钮 + 下载按钮） -->
    <div v-if="docs.length > 0" class="pav-doc-section">
      <div v-if="showSectionTitle" class="pav-section-title">
        <i class="el-icon-document"></i>
        <span>附件文档资料</span>
        <small class="pav-count">({{ docs.length }})</small>
      </div>
      <div class="pav-file-list" :class="{ 'is-compact': layout === 'compact' }">
        <div v-for="file in docs" :key="file.Id || file.FileUrl" class="pav-file-item">
          <!-- 文件主体点击：直接预览 -->
          <div class="pav-file-main" @click="handlePreview(file)">
            <div class="pav-file-icon" :class="getFileIconClass(file)">
              <i :class="getFileIcon(file)"></i>
            </div>
            <div class="pav-file-info">
              <div class="pav-fn" :title="file.FileName || fileNameFromUrl(file.FileUrl)">
                {{ file.FileName || fileNameFromUrl(file.FileUrl) }}
              </div>
              <div class="pav-fm">
                {{ formatAttachmentSize(file) || getFileExtDesc(file) }}
              </div>
            </div>
          </div>

          <!-- 操作按钮组：在线预览 + 本地下载 -->
          <div class="pav-file-actions">
            <el-tooltip content="在线预览" placement="top" :open-delay="300">
              <div class="pav-action-btn" @click="handlePreview(file)">
                <i class="el-icon-view"></i>
              </div>
            </el-tooltip>
            <el-tooltip content="点击下载" placement="top" :open-delay="300">
              <oss-media-link :path="file.FileUrl" :download="true" class="pav-dl-link">
                <div class="pav-action-btn is-download">
                  <i class="el-icon-download"></i>
                </div>
              </oss-media-link>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. 文件全屏/宽屏在线预览弹窗（工作记录同款） -->
    <el-dialog
      title="文件预览"
      :visible.sync="previewVisible"
      width="92vw"
      top="3vh"
      custom-class="pav-preview-dialog"
      append-to-body
      :close-on-click-modal="false"
      @closed="onPreviewClosed"
    >
      <div class="pav-preview-container">
        <div class="pav-preview-toolbar">
          <div class="pav-toolbar-left">
            <i class="el-icon-document" style="color: #409eff; margin-right: 6px;"></i>
            <span class="pav-preview-filename" :title="currentPreviewName">{{ currentPreviewName }}</span>
          </div>
          <div class="pav-toolbar-right">
            <a v-if="previewUrlRaw" :href="previewUrlRaw" target="_blank" rel="noopener">
              <el-button icon="el-icon-full-screen" type="text" size="small">新窗口全屏打开</el-button>
            </a>
            <oss-media-link v-if="currentFile && currentFile.FileUrl" :path="currentFile.FileUrl" :download="true" style="margin-left: 12px;">
              <el-button icon="el-icon-download" type="primary" size="mini">下载文件</el-button>
            </oss-media-link>
          </div>
        </div>
        <div class="pav-preview-frame-wrapper">
          <embed
            v-if="previewVisible && previewIsPdf"
            :src="previewUrl"
            type="application/pdf"
            class="pav-preview-iframe"
          />
          <iframe
            v-else-if="previewVisible"
            :src="previewUrl"
            frameborder="0"
            class="pav-preview-iframe"
          ></iframe>
        </div>
      </div>
    </el-dialog>

    <!-- 4. 预览环境受限提示弹窗（内网环境 Office 在线预览无法访问提示，工作记录同款） -->
    <el-dialog
      :visible.sync="previewHintVisible"
      width="480px"
      append-to-body
      custom-class="pav-hint-dialog"
      :show-close="true"
      :close-on-click-modal="false"
      @closed="onPreviewHintClosed"
    >
      <div class="pav-hint">
        <div class="pav-hint__hero">
          <div class="pav-hint__icon" aria-hidden="true">
            <i class="el-icon-warning-outline"></i>
          </div>
          <h3 class="pav-hint__title">预览环境受限</h3>
          <p class="pav-hint__desc">
            当前处于<strong>私有内网或本地调试环境</strong>，微软 Office 在线云端服务无法直接访问内部文件，请下载至本地打开查看。
          </p>
        </div>
        <div class="pav-hint__steps">
          <div class="pav-hint__step">
            <span class="pav-hint__step-no">1</span>
            <div class="pav-hint__step-body">
              <div class="pav-hint__step-title">下载到本地查看</div>
              <div class="pav-hint__step-text">点击下方下载按钮，使用本地 Office / WPS 浏览。</div>
            </div>
          </div>
          <div class="pav-hint__step">
            <span class="pav-hint__step-no">2</span>
            <div class="pav-hint__step-body">
              <div class="pav-hint__step-title">公网部署后自动可用</div>
              <div class="pav-hint__step-text">系统正式部署到具有公网域名的服务器后，Office 在线免装预览将自动生效。</div>
            </div>
          </div>
        </div>
        <div v-if="previewHintFile" class="pav-hint__file">
          <i :class="getFileIcon(previewHintFile)"></i>
          <span class="pav-hint__file-name">{{ previewHintFile.FileName || fileNameFromUrl(previewHintFile.FileUrl) }}</span>
        </div>
      </div>
      <span slot="footer" class="pav-hint__footer">
        <el-button size="small" @click="previewHintVisible = false">知道了</el-button>
        <oss-media-link
          v-if="previewHintFile"
          :path="previewHintFile.FileUrl"
          :download="true"
        >
          <el-button type="primary" size="small" icon="el-icon-download">立即下载文件</el-button>
        </oss-media-link>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 心理模块附件与图片标准化浏览组件（参考工作记录详情交互设计）。创建于 2026-09-07
 * 支持：
 * 1. 图片缩略网格 + 点击全屏大图预览（OssElImage + 预签名）
 * 2. 文档卡片化排列（PDF/Word/Excel/PPT/TXT 图标、大小展示）
 * 3. 一键在线预览（PDF embed、Office 微软云端预览、内网安全引导）
 * 4. 一键直链下载（OssMediaLink）
 */
import {
  isAliyunOssHttpsUrl,
  stripOssUrlQuery,
  resolveAttachmentAccessUrl,
  fileExtFromMedia,
} from '../../utils/mediaUrl';
import { getUploadSchoolId } from '../../utils/ossMediaUpload';

export default {
  name: 'PsychologyAttachmentViewer',
  props: {
    /** 附件列表，支持对象数组 [{ FileUrl, FileName, FileType, FileSize }] 或 URL 字符串数组 */
    attachments: {
      type: Array,
      default: () => []
    },
    /** 学校 ID（可选，默认自动识别） */
    schoolId: {
      type: [Number, String],
      default: 0
    },
    /** 布局形态：'full' 完整展示，'compact' 紧凑展示（如时间线小卡片） */
    layout: {
      type: String,
      default: 'compact'
    },
    /** 缩略图宽度/高度，默认 64px（compact）或 88px（full） */
    imageSize: {
      type: [Number, String],
      default: ''
    },
    /** 是否展示板块标题 */
    showSectionTitle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      previewVisible: false,
      previewUrl: '',
      previewUrlRaw: '',
      previewIsPdf: false,
      currentPreviewName: '',
      currentFile: null,

      previewHintVisible: false,
      previewHintFile: null
    };
  },
  computed: {
    effectiveSchoolId() {
      return this.schoolId || getUploadSchoolId(this) || 0;
    },
    normalizedList() {
      if (!this.attachments || !Array.isArray(this.attachments)) return [];
      return this.attachments
        .map(item => {
          if (!item) return null;
          if (typeof item === 'string') {
            return {
              Id: item,
              FileUrl: item,
              FileName: this.fileNameFromUrl(item),
              FileType: '',
              FileSize: 0
            };
          }
          return {
            Id: item.Id || item.id || 0,
            FileUrl: item.FileUrl || item.fileUrl || item.Url || item.url || '',
            FileName: item.FileName || item.fileName || item.Name || item.name || '',
            FileType: item.FileType || item.fileType || '',
            FileSize: item.FileSize || item.fileSize || 0
          };
        })
        .filter(item => item && item.FileUrl);
    },
    images() {
      return this.normalizedList.filter(item => this.isImage(item));
    },
    imageUrls() {
      return this.images.map(item => item.FileUrl);
    },
    docs() {
      return this.normalizedList.filter(item => !this.isImage(item));
    },
    hasItems() {
      return this.normalizedList.length > 0;
    },
    imgSizePx() {
      if (this.imageSize) {
        return typeof this.imageSize === 'number' ? `${this.imageSize}px` : this.imageSize;
      }
      return this.layout === 'compact' ? '56px' : '88px';
    }
  },
  methods: {
    isImage(file) {
      if (!file) return false;
      const ext = fileExtFromMedia(file.FileUrl, file.FileType);
      const e = (ext || '').toLowerCase();
      if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'].includes(e)) return true;
      if (file.FileUrl && (file.FileUrl.startsWith('data:image/') || /\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(file.FileUrl))) {
        return true;
      }
      return false;
    },

    fileNameFromUrl(url) {
      const clean = String(url || '').split('?')[0].split('#')[0];
      const name = clean.split('/').pop() || '附件资料';
      try {
        return decodeURIComponent(name);
      } catch (_) {
        return name;
      }
    },

    getFileIcon(file) {
      const ext = fileExtFromMedia(file && file.FileUrl, file && file.FileType);
      const e = (ext || '').toLowerCase();
      if (['.doc', '.docx'].includes(e)) return 'el-icon-document';
      if (['.xls', '.xlsx', '.csv'].includes(e)) return 'el-icon-s-grid';
      if (['.ppt', '.pptx'].includes(e)) return 'el-icon-monitor';
      if (e === '.pdf') return 'el-icon-reading';
      if (e === '.txt') return 'el-icon-document-copy';
      if (['.zip', '.rar', '.7z', '.tar', '.gz'].includes(e)) return 'el-icon-box';
      return 'el-icon-files';
    },

    getFileIconClass(file) {
      const ext = fileExtFromMedia(file && file.FileUrl, file && file.FileType);
      const e = (ext || '').toLowerCase();
      if (['.doc', '.docx'].includes(e)) return 'is-word';
      if (['.xls', '.xlsx', '.csv'].includes(e)) return 'is-excel';
      if (['.ppt', '.pptx'].includes(e)) return 'is-ppt';
      if (e === '.pdf') return 'is-pdf';
      if (e === '.txt') return 'is-txt';
      if (['.zip', '.rar', '.7z'].includes(e)) return 'is-zip';
      return 'is-generic';
    },

    getFileExtDesc(file) {
      const ext = fileExtFromMedia(file && file.FileUrl, file && file.FileType);
      if (!ext) return '附件文档';
      return ext.replace('.', '').toUpperCase() + ' 文档';
    },

    formatAttachmentSize(file) {
      const size = file && file.FileSize;
      if (size == null || size === '' || Number(size) <= 0) return '';
      const bytes = Number(size);
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async handlePreview(file) {
      if (!file || !file.FileUrl) return;
      const rawUrl = String(file.FileUrl || '').trim();
      const stableUrl = stripOssUrlQuery(rawUrl);
      const schoolId = this.effectiveSchoolId;

      const ext = fileExtFromMedia(stableUrl || rawUrl, file.FileType);
      const e = (ext || '').toLowerCase();
      const isPdf = e === '.pdf';
      const isOffice = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'].includes(e);
      const isTxt = e === '.txt';

      this.currentPreviewName = file.FileName || this.fileNameFromUrl(rawUrl);
      this.currentFile = file;

      const loadAccessUrl = async () => {
        return resolveAttachmentAccessUrl(stableUrl || rawUrl, schoolId, { inline: true });
      };

      if (isOffice) {
        let isInternal = false;
        try {
          const url = new URL(rawUrl.startsWith('http') ? rawUrl : window.location.href);
          const host = url.hostname;
          isInternal =
            host === 'localhost' ||
            host === '127.0.0.1' ||
            host.startsWith('192.168.') ||
            host.startsWith('10.') ||
            (host.startsWith('172.') &&
              parseInt(host.split('.')[1], 10) >= 16 &&
              parseInt(host.split('.')[1], 10) <= 31);
        } catch (_) {
          isInternal = true;
        }

        if (isInternal) {
          this.previewHintFile = file;
          this.previewHintVisible = true;
          return;
        }

        const loading = this.$loading({ lock: true, text: '正在加载文档预览...' });
        try {
          const accessUrl = await loadAccessUrl();
          this.previewUrlRaw = accessUrl;
          this.previewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(accessUrl)}`;
          this.previewIsPdf = false;
          this.previewVisible = true;
        } catch (err) {
          this.$message.error((err && err.message) || '获取预览地址失败，请直接下载后查看');
        } finally {
          loading.close();
        }
        return;
      }

      const needSign = isPdf || isTxt || isAliyunOssHttpsUrl(stableUrl);
      if (needSign) {
        const loading = this.$loading({ lock: true, text: '正在加载文件预览...' });
        try {
          const accessUrl = await loadAccessUrl();
          this.previewUrlRaw = accessUrl;
          this.previewUrl = accessUrl;
          this.previewIsPdf = isPdf;
          this.previewVisible = true;
        } catch (err) {
          this.$message.error((err && err.message) || '获取预览地址失败，请直接下载后查看');
        } finally {
          loading.close();
        }
        return;
      }

      this.previewUrlRaw = rawUrl;
      this.previewUrl = rawUrl;
      this.previewIsPdf = isPdf;
      this.previewVisible = true;
    },

    onPreviewClosed() {
      this.previewIsPdf = false;
      this.previewUrl = '';
      this.previewUrlRaw = '';
      this.currentPreviewName = '';
      this.currentFile = null;
    },

    onPreviewHintClosed() {
      this.previewHintFile = null;
    }
  }
};
</script>

<style scoped>
.psychology-attachment-viewer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pav-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.pav-section-title i {
  color: #4f46e5;
  font-size: 14px;
}

.pav-count {
  font-weight: 400;
  color: #94a3b8;
  font-size: 12px;
}

/* 图片区域 */
.pav-image-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pav-image-item {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  background: #f8fafc;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pav-image-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #cbd5e1;
}

/* 附件文件列表（工作记录同款精美条目） */
.pav-file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pav-file-list.is-compact {
  gap: 6px;
}

.pav-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.pav-file-item:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.pav-file-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.pav-file-icon {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-right: 10px;
  flex-shrink: 0;
  background: #f1f5f9;
  color: #64748b;
}

.pav-file-icon.is-word {
  background: #eff6ff;
  color: #2563eb;
}

.pav-file-icon.is-excel {
  background: #ecfdf5;
  color: #059669;
}

.pav-file-icon.is-ppt {
  background: #fff7ed;
  color: #ea580c;
}

.pav-file-icon.is-pdf {
  background: #fef2f2;
  color: #dc2626;
}

.pav-file-icon.is-txt {
  background: #f8fafc;
  color: #475569;
}

.pav-file-icon.is-zip {
  background: #faf5ff;
  color: #9333ea;
}

.pav-file-info {
  flex: 1;
  min-width: 0;
}

.pav-fn {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pav-fm {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.pav-file-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  flex-shrink: 0;
}

.pav-action-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: #f8fafc;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  transition: all 0.15s ease;
  font-size: 14px;
}

.pav-action-btn:hover {
  background: #eef2ff;
  color: #4f46e5;
  border-color: #c7d2fe;
}

.pav-action-btn.is-download:hover {
  background: #ecfdf5;
  color: #10b981;
  border-color: #a7f3d0;
}

.pav-dl-link {
  text-decoration: none;
  display: inline-block;
}

/* 提示弹窗内部 */
.pav-hint__hero {
  text-align: center;
  padding: 4px 8px 0;
}

.pav-hint__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: linear-gradient(145deg, #fff7e6, #ffe7ba);
  color: #e6a23c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.pav-hint__title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
}

.pav-hint__desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #64748b;
}

.pav-hint__steps {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 14px;
}

.pav-hint__step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.pav-hint__step-no {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.pav-hint__step-title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.pav-hint__step-text {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.pav-hint__file {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #475569;
  font-size: 13px;
}

.pav-hint__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pav-hint__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>

<style>
/* 预览大弹窗全局样式 */
.pav-preview-dialog {
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.2) !important;
  height: 92vh !important;
  display: flex !important;
  flex-direction: column !important;
  z-index: 3500 !important;
}

.pav-preview-dialog .el-dialog__header {
  padding: 12px 20px !important;
  background: #ffffff !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pav-preview-dialog .el-dialog__body {
  padding: 0 !important;
  flex: 1 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  max-height: none !important;
  background: #f1f5f9 !important;
}

.pav-preview-container {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
}

.pav-preview-toolbar {
  height: 44px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0 16px !important;
  background: #ffffff !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pav-toolbar-left {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 60%;
}

.pav-preview-filename {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: #1e293b !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pav-toolbar-right {
  display: flex;
  align-items: center;
}

.pav-preview-frame-wrapper {
  flex: 1 !important;
  background: #f8fafc !important;
  position: relative;
}

.pav-preview-iframe {
  width: 100% !important;
  height: 100% !important;
  border: none !important;
}

/* 提示弹窗样式 */
.el-dialog.pav-hint-dialog {
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.16) !important;
  z-index: 3600 !important;
}

.el-dialog.pav-hint-dialog .el-dialog__header {
  padding: 14px 18px 0 !important;
  border-bottom: none !important;
  background: #fff !important;
}

.el-dialog.pav-hint-dialog .el-dialog__footer {
  padding: 12px 20px 16px !important;
  border-top: 1px solid #f1f5f9;
  background: #fff !important;
}
</style>
